'use client';

/**
 * MainSidebar - Universal Navigation Sidebar
 *
 * The one navigation sidebar for all pages: the compact icon rail (w-14), the
 * same rail the officer sees during a call (Q-1170, Andrew 2026-10-07: "it
 * should be the version we see when we are on a call... i had asked for the
 * other version to be removed. its legacy."). There is no expanded mode.
 * - Full-height, fills screen edge to edge (no footer beside it)
 * - Names live in aria-label and the hover/focus tooltip or flyout
 * - A group with several items opens a flyout (hover or keyboard focus) that
 *   lists every item and child, so no destination is lost with the rail;
 *   open/close rules live in useRailFlyouts
 * - Consistent color-coded sections
 *
 * Uses data-driven navigation from `@/lib/navigation`:
 * - Groups and items fetched from database
 * - Supports agency/department overrides
 * - Icons resolved dynamically from string names
 */

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Loader2,
  CircleSlash,
  Pencil,
} from 'lucide-react';
import {
  useIsAuthenticated,
  useIsStaff,
  useAdminStatus,
} from '../AppShellContext';
import { useSidebarNavigation, resolveIcon } from '@/lib/navigation';
import { isNavGatePassed, type NavAccessState } from '@/lib/navigation/utils';
import { useUserPreference } from '@/hooks/useUserPreferences';
import { getColorScheme, type NavColorScheme, type ColorSchemeConfig } from '@/lib/ui/colorSchemes';
import type { ResolvedNavGroup, ResolvedNavItem } from '@/lib/navigation';
import { InstantTooltip } from '@/components/ui/InstantTooltip';
import { useRailFlyouts, type RailFlyouts } from './useRailFlyouts';

// ============================================================================
// Types
// ============================================================================

interface MainSidebarProps {
  className?: string;
}

// Group keys that begin a new visual cluster in the sidebar. A faint horizontal
// divider is rendered directly above any group whose key appears here. Keep this
// list in sync with the cluster layout documented in the render loop below.
const CLUSTER_START_KEYS: ReadonlySet<string> = new Set([
  'calls',   // starts "services" cluster (after home)
  'reports', // starts "results & data" cluster (after training)
  'admin',   // starts "settings" cluster (after library)
]);

// ============================================================================
// Tooltip content helper
// ============================================================================

function GroupTooltipContent({ label, description }: { label: string; description?: string }) {
  return (
    <div>
      <div className="font-medium text-white">{label}</div>
      {description && <div className="text-xs text-white/70 mt-0.5">{description}</div>}
    </div>
  );
}

// ============================================================================
// Color Helper - Uses shared color schemes
// ============================================================================

function getColorConfig(colorScheme: NavColorScheme | undefined) {
  return getColorScheme(colorScheme, 'blue');
}

function toGroupHoverBg(bgClass: string) {
  return bgClass.replace(/\bbg-/g, 'group-hover:bg-');
}

// ============================================================================
// Component
// ============================================================================

/**
 * Recursively filter a nav item's children by access. Returns null if
 * the item itself is gated out, otherwise returns a copy with children
 * filtered. Defense-in-depth — server filter is authoritative.
 */
function filterItemByAccess(
  item: ResolvedNavItem,
  access: NavAccessState,
): ResolvedNavItem | null {
  if (!isNavGatePassed(item, access)) return null;

  if (!item.children || item.children.length === 0) return item;

  const filteredChildren = item.children
    .map((child) => filterItemByAccess(child, access))
    .filter((child): child is ResolvedNavItem => child !== null);

  return { ...item, children: filteredChildren };
}

export function MainSidebar({
  className = '',
}: MainSidebarProps) {
  const pathname = usePathname();
  // Only one group's flyout is open at a time; a route change closes it.
  const flyouts = useRailFlyouts(pathname);
  const isAuthenticated = useIsAuthenticated();
  const isStaff = useIsStaff();
  const adminStatus = useAdminStatus();
  // Use the data-driven navigation hook
  const {
    groups,
    isLoading,
    error,
    refresh,
  } = useSidebarNavigation();

  // Defense-in-depth client-side ACL: hide groups/items the current user
  // shouldn't see (e.g. admin-only sections for non-admin staff). The
  // server filter in /api/navigation is authoritative — this prevents any
  // entries that slip past (cached payloads, child items under a parent
  // that itself passes the role check, etc.) from being visible.
  // See issue #247.
  const access: NavAccessState = {
    isAuthenticated,
    isStaff,
    isAdmin: adminStatus.isAdmin,
    isPlatformAdmin: adminStatus.isPlatformAdmin,
  };

  // Compute visibleGroups BEFORE any early-return so hook count stays stable
  // across renders where isStaff flips (auth loading → loaded). Calling
  // React.useMemo conditionally violates the rules of hooks and causes
  // React error #310 on the first render after auth resolves.
  const visibleGroups = React.useMemo(() => {
    return groups
      .map((group) => {
        if (!isNavGatePassed(group, access)) return null;
        const filteredItems = group.items
          .map((item) => filterItemByAccess(item, access))
          .filter((item): item is ResolvedNavItem => item !== null);
        // If a group has no remaining items AND no direct primaryPath, hide it
        if (filteredItems.length === 0 && !group.primaryPath) return null;
        return { ...group, items: filteredItems };
      })
      .filter((group): group is ResolvedNavGroup => group !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, access.isAuthenticated, access.isStaff, access.isAdmin, access.isPlatformAdmin]);

  // Only show sidebar for staff users (early return AFTER all hooks)
  if (!isStaff) {
    return null;
  }

  return (
    <aside
      aria-label="Sidebar"
      data-demo-id="main-sidebar"
      className={`hidden sm:flex flex-col border-r border-white/[0.08] w-14 h-full ${className}`}
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Navigation Groups */}
      {/* Scrolls when the groups outgrow the viewport; no visible scrollbar, which would take a third of the 56px rail. */}
      <nav aria-label="Main" className="flex-1 py-0.5 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-white/70" />
          </div>
        ) : error ? (
          <div role="alert" className="flex flex-col items-center justify-center py-8 px-1 text-center">
            <CircleSlash className="w-5 h-5 text-blue-400/60 mb-2" aria-hidden="true" />
            <button
              onClick={() => void refresh()}
              aria-label="Failed to load navigation. Retry"
              title="Failed to load navigation. Retry"
              className="text-[10px] text-blue-400 hover:text-blue-300 underline cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-1 text-center" title="No navigation items">
            <CircleSlash className="w-5 h-5 text-white/40" aria-hidden="true" />
            <span className="sr-only">No navigation items</span>
          </div>
        ) : (
          visibleGroups.map((group, index) => {
            // Show a divider BEFORE this group only when it starts a new cluster.
            // Clusters (top-to-bottom):
            //   1. home                           — {home}
            //   2. services                       — {calls, training}
            //   3. results & data                 — {reports, analytics, library}
            //   4. settings                       — {admin, settings}
            // So dividers appear before calls, reports, and admin (not between
            // groups inside the same cluster).
            const showDividerBefore = index > 0 && CLUSTER_START_KEYS.has(group.key);

            return (
              <React.Fragment key={group.key}>
                {showDividerBefore && (
                  <div className="my-2 mx-3 border-t border-white/[0.15]" />
                )}
                {/* Leaf mode: groups with primaryPath navigate directly instead of expanding */}
                {group.primaryPath ? (
                  <SidebarLeafGroup group={group} pathname={pathname} />
                ) : (
                  <SidebarGroup group={group} flyouts={flyouts} />
                )}
              </React.Fragment>
            );
          })
        )}
      </nav>

    </aside>
  );
}

// ============================================================================
// Rail icon (shared by leaf and multi-item groups)
// ============================================================================

interface RailIconProps {
  GroupIcon: React.ElementType;
  colors: ColorSchemeConfig;
  active: boolean;
  badge?: React.ReactNode;
}

function RailIcon({ GroupIcon, colors, active, badge }: RailIconProps) {
  const hoverIconBg = toGroupHoverBg(colors.iconBg);
  const iconState = active ? colors.iconColor : 'text-white/70';
  const outerState = active
    ? 'bg-white/[0.06] ring-1 ring-white/[0.06]'
    : 'hover:bg-white/[0.06]';
  const innerState = active ? 'bg-white/[0.08]' : 'bg-white/[0.04]';
  return (
    <div
      className={`
        relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200
        ${outerState}
      `}
    >
      <div className={`
        p-0.5 rounded-lg transition-all duration-200 ${innerState} ${hoverIconBg} group-hover:scale-105
      `}>
        <GroupIcon className={`w-7 h-7 transition-colors duration-200 ${iconState} ${colors.hoverText}`} aria-hidden="true" />
      </div>
      {badge}
    </div>
  );
}

// ============================================================================
// SidebarLeafGroup Component — Direct navigation (e.g. Home → /dashboard)
// ============================================================================

interface SidebarLeafGroupProps {
  group: ResolvedNavGroup;
  pathname: string;
}

function SidebarLeafGroup({ group, pathname }: SidebarLeafGroupProps) {
  const colors = getColorConfig(group.colorScheme);
  const GroupIcon = resolveIcon(group.iconName) || CircleSlash;
  const isHome = group.key === 'home';

  // Custom home page destination — users can pick their preferred landing page
  const [homePagePath] = useUserPreference<string>('home_page_path', '/dashboard');
  const effectivePath = isHome ? (homePagePath || group.primaryPath!) : group.primaryPath!;
  const isActive = pathname === effectivePath || (isHome && pathname === '/dashboard');

  // Edit badge for home button — links to settings to change home page.
  // Visible chrome stays at 16×16 to preserve layout, but a transparent
  // ::before pseudo-element expands the click target to 24×24 so the
  // interactive surface meets WCAG 2.5.8 (Target Size, Minimum, Level AA).
  const editBadge = isHome && (
    <Link
      href="/settings"
      prefetch={false}
      onClick={(e) => e.stopPropagation()}
      aria-label="Change home page"
      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-600 hover:border-blue-500 z-10 before:absolute before:content-[''] before:-inset-1 before:rounded-full"
      title="Change home page"
    >
      <Pencil className="w-2 h-2 text-white" aria-hidden="true" />
    </Link>
  );

  return (
    <div className="mb-0 px-1">
      <InstantTooltip
        position="right"
        content={<GroupTooltipContent label={group.label} description={group.description} />}
      >
        <Link
          href={effectivePath}
          prefetch={false}
          aria-label={group.label}
          data-demo-id={`sidebar-group-${group.key}`}
          className="flex items-center justify-center group"
        >
          <RailIcon GroupIcon={GroupIcon} colors={colors} active={isActive} badge={editBadge} />
        </Link>
      </InstantTooltip>
    </div>
  );
}

// ============================================================================
// SidebarGroup Component — rail icon plus a flyout listing every item
// ============================================================================

interface SidebarGroupProps {
  group: ResolvedNavGroup;
  flyouts: RailFlyouts;
}

/** Width of the rail (w-14), the flyout's left edge when the rail cannot be measured. */
const RAIL_WIDTH_PX = 56;
const FLYOUT_VIEWPORT_MARGIN_PX = 8;

function SidebarGroup({ group, flyouts }: SidebarGroupProps) {
  const colors = getColorConfig(group.colorScheme);
  const GroupIcon = resolveIcon(group.iconName) || CircleSlash;
  const key = group.key;
  const open = flyouts.openKey === key;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLAnchorElement>(null);
  const flyoutRef = React.useRef<HTMLDivElement>(null);
  // 'touch' once a touch pointer was seen: browsers fire emulated mouse events on a tap, which must not count as hover.
  // A key press resets it, so Enter on the icon after an earlier tap navigates instead of acting as a first tap.
  const pointerTypeRef = React.useRef<string>('mouse');
  // Whether the flyout was already open when the current touch began. A tap's emulated mousedown/focus opens the flyout
  // BEFORE the click fires, so "open at click time" cannot tell a first tap from a second one.
  const openAtPointerDownRef = React.useRef(false);
  // Set by ArrowDown/ArrowRight on the icon: move focus to the first flyout link once it renders.
  const focusFirstOnOpenRef = React.useRef(false);
  const flyoutId = React.useId();
  const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: RAIL_WIDTH_PX });

  const hasActiveItem = group.items.some(
    item => item.isActive || item.children?.some(child => child.isActive)
  );
  const firstItemPath = group.items[0]?.path || '/';

  // Place the flyout beside the icon, flush with the rail (no hover gap), shifted up when it would run off the
  // bottom of the viewport. Re-measured on resize/zoom and on any scroll (the rail itself scrolls).
  React.useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const place = () => {
      if (!wrapperRef.current) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const flyoutHeight = flyoutRef.current?.offsetHeight ?? 0;
      const maxTop = Math.max(FLYOUT_VIEWPORT_MARGIN_PX, window.innerHeight - flyoutHeight - FLYOUT_VIEWPORT_MARGIN_PX);
      setPosition({
        top: Math.min(Math.max(wrapperRect.top, FLYOUT_VIEWPORT_MARGIN_PX), maxTop),
        left: wrapperRect.right || RAIL_WIDTH_PX,
      });
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || !focusFirstOnOpenRef.current) return;
    focusFirstOnOpenRef.current = false;
    flyoutRef.current?.querySelector<HTMLElement>('a[href]')?.focus();
  }, [open]);

  // Close the flyout, handing focus back to the rail icon first if it is inside the flyout (so it never drops to <body>).
  const closeKeepingFocus = React.useCallback(() => {
    if (flyoutRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    flyouts.close(key);
  }, [flyouts, key]);

  // While open: tapping or clicking anywhere outside the group closes it (the only way to dismiss on touch), and
  // Escape closes it wherever focus is (WCAG 1.4.13: a hover-opened flyout is dismissible without moving focus).
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: Event) => {
      if (!wrapperRef.current?.contains(event.target as Node | null)) flyouts.close(key);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeKeepingFocus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, flyouts, key, closeKeepingFocus]);

  const notePointer = (event: React.PointerEvent) => {
    pointerTypeRef.current = event.pointerType || 'mouse';
  };
  const handlePointerDown = (event: React.PointerEvent) => {
    notePointer(event);
    openAtPointerDownRef.current = open;
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) flyouts.focusLeave(key);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    pointerTypeRef.current = 'keyboard';
    // ArrowDown/ArrowRight on the icon opens the flyout and moves into it (the way back in after Escape).
    if ((event.key === 'ArrowDown' || event.key === 'ArrowRight') && event.target === triggerRef.current) {
      event.preventDefault();
      if (open) flyoutRef.current?.querySelector<HTMLElement>('a[href]')?.focus();
      else {
        focusFirstOnOpenRef.current = true;
        flyouts.open(key);
      }
    }
  };
  const handleTriggerClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Touch: the first tap opens the flyout (so every item is reachable), the second tap navigates.
    if (pointerTypeRef.current === 'touch' && !openAtPointerDownRef.current) {
      event.preventDefault();
      flyouts.open(key);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative mb-0 px-1"
      onPointerDown={handlePointerDown}
      onPointerEnter={notePointer}
      onMouseEnter={() => {
        if (pointerTypeRef.current !== 'touch') flyouts.pointerEnter(key);
      }}
      onMouseLeave={(event) => {
        const to = event.relatedTarget;
        const withinRail = to instanceof Node && !!wrapperRef.current?.closest('nav')?.contains(to);
        if (pointerTypeRef.current !== 'touch') flyouts.pointerLeave(key, withinRail);
      }}
      onFocus={() => {
        if (wrapperRef.current) flyouts.focusEnter(key, wrapperRef.current);
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <Link
        ref={triggerRef}
        href={firstItemPath}
        prefetch={false}
        aria-label={group.label}
        aria-expanded={open}
        aria-controls={open ? flyoutId : undefined}
        onClick={handleTriggerClick}
        data-demo-id={`sidebar-group-${group.key}`}
        data-testid={group.key === 'library' ? 'sidebar-section-library' : undefined}
        className="flex items-center justify-center group"
      >
        <RailIcon GroupIcon={GroupIcon} colors={colors} active={hasActiveItem} />
      </Link>

      {open && (
        <div
          ref={flyoutRef}
          id={flyoutId}
          role="group"
          aria-label={`${group.label} pages`}
          data-demo-id={`sidebar-flyout-${group.key}`}
          onClick={(event) => {
            // Following a flyout link closes the flyout (the rail persists across client navigations).
            if ((event.target as HTMLElement).closest('a')) closeKeepingFocus();
          }}
          className="fixed z-50 w-60 max-h-[calc(100vh-16px)] overflow-y-auto rounded-r-xl border border-l-0 border-white/[0.08] py-1.5 shadow-xl shadow-black/60"
          style={{ backgroundColor: '#0a0a0a', top: position.top, left: position.left }}
        >
          <div className="px-3 pb-1 pt-0.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-white/70 uppercase tracking-wider">
              <div className={`w-1 h-1 rounded-full ${colors.dot} opacity-60`} />
              <span>{group.label}</span>
            </div>
            {group.description && <div className="mt-0.5 text-xs text-white/70">{group.description}</div>}
          </div>
          <div className="px-1.5 pb-0.5">
            {group.items.map((item) => (
              <FlyoutItem key={item.key} item={item} colors={colors} depth={0} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FlyoutItem Component — one link per destination, children nested beneath
// ============================================================================

interface FlyoutItemProps {
  item: ResolvedNavItem;
  colors: ColorSchemeConfig;
  depth: number;
}

function FlyoutItem({ item, colors, depth }: FlyoutItemProps) {
  const Icon = resolveIcon(item.iconName) || CircleSlash;
  const isActive = item.isActive;
  const hasChildren = !!item.children && item.children.length > 0 && depth < 2;
  const hasActiveChild = hasChildren && item.children!.some(child => child.isActive);
  const highlighted = isActive || hasActiveChild;

  return (
    <div>
      <Link
        href={item.path}
        prefetch={false}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
        data-demo-id={`sidebar-${item.key}`}
        data-testid={item.path === '/knowledge-base' ? 'sidebar-link-knowledge-base' : undefined}
        aria-current={isActive ? 'page' : hasActiveChild ? 'true' : undefined}
        className={`
          flex items-center gap-2 px-2 ${depth > 0 ? 'py-1 text-xs' : 'py-1.5 text-sm'} rounded-lg transition-all duration-200 w-full border-l-2 border-transparent group
          ${isActive ? `${colors.activeBg} ${colors.activeBorder}` : hasActiveChild ? `${colors.activeBg} border-transparent` : `hover:bg-white/[0.04] ${colors.hoverBg}`}
        `}
      >
        {depth === 0 ? (
          <div className={`p-1 rounded-md ${colors.iconBg} group-hover:scale-105 transition-all duration-200`}>
            <Icon className={`w-5 h-5 flex-shrink-0 ${colors.iconColor}`} aria-hidden="true" />
          </div>
        ) : (
          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? colors.iconColor : 'text-white/70'}`} aria-hidden="true" />
        )}
        <span className={`truncate transition-colors duration-200 ${highlighted ? colors.activeText : `text-white/60 ${colors.hoverText}`}`}>
          {item.label}
        </span>
        {item.badge && (
          <span className={`
            ml-auto px-1.5 py-0.5 text-[10px] font-medium rounded flex-shrink-0
            ${item.badge.color || 'bg-white/10 text-white/60'}
          `}>
            {item.badge.value}
          </span>
        )}
        {isActive && depth > 0 && !item.badge && (
          <div className={`ml-auto w-1 h-1 rounded-full ${colors.dot}`} />
        )}
      </Link>
      {hasChildren && (
        <div className={`ml-3 pl-1.5 border-l border-white/[0.06] ${item.children!.length > 12 ? 'max-h-64 overflow-y-auto' : ''}`}>
          {item.children!.map((child) => (
            <FlyoutItem key={child.key} item={child} colors={colors} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default MainSidebar;
